/**
 * Browser-side cohort metadata enrichment for RudderStack track / page calls.
 *
 * Why this exists: PostHog cannot resolve cohort_id → cohort_name out of the
 * box. Server-side tracking (core/tracking/client.ts) snapshots cohort
 * metadata onto events at emission time. Browser-emitted events bypass that
 * path entirely (they go directly RudderStack browser SDK → RudderStack data
 * plane), so we mirror the same enrichment here by fetching the bundle once
 * per cohort per session.
 *
 * Endpoint: GET /api-proxy/cohort-metadata/:cohortId on the dispatcher,
 * served by server/edge/external/routes.ts. Returns the 9-field bundle.
 *
 * Cache: in-memory Map (per page load) + sessionStorage (per browser tab),
 * single-flight on inflight requests, 30 min positive TTL, 1 min negative
 * TTL on resolution failures.
 *
 * See plans/260521_cohort_name_in_posthog.md Phase 4b.
 */

const POSITIVE_TTL_MS = 30 * 60 * 1000;
const NEGATIVE_TTL_MS = 60 * 1000;
const SESSION_KEY_PREFIX = "cohort-metadata:";
const ENDPOINT_PREFIX = "/api-proxy/cohort-metadata/";

export type CohortBundle = {
  cohort_name?: string;
  cohort_start_date?: string | null;
  cohort_end_date?: string | null;
  cohort_type?: string;
  cohort_version_id?: string;
  program_id?: string;
  program_name?: string;
  mindspace_id?: string;
  mindspace_name?: string;
};

type CacheEntry = { data: CohortBundle | null; expiresAt: number };

const memCache: Map<string, CacheEntry> = new Map();
const inflight: Map<string, Promise<CohortBundle | null>> = new Map();

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const target = name + "=";
  const parts = document.cookie.split(";");
  for (const raw of parts) {
    const part = raw.trim();
    if (part.startsWith(target)) {
      return decodeURIComponent(part.slice(target.length));
    }
  }
  return null;
}

function readSessionCache(cohortId: string): CacheEntry | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY_PREFIX + cohortId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry;
    if (typeof parsed?.expiresAt !== "number" || parsed.expiresAt < Date.now()) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeSessionCache(cohortId: string, entry: CacheEntry): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      SESSION_KEY_PREFIX + cohortId,
      JSON.stringify(entry),
    );
  } catch {
    // sessionStorage quota or disabled — ignore silently.
  }
}

async function fetchBundle(cohortId: string): Promise<CohortBundle | null> {
  const token = readCookie("x-ms-access-token");
  if (!token) return null;
  let res: Response;
  try {
    res = await fetch(ENDPOINT_PREFIX + encodeURIComponent(cohortId), {
      method: "GET",
      headers: { authorization: "Bearer " + token },
      credentials: "same-origin",
    });
  } catch {
    return null;
  }
  if (!res.ok) return null;
  try {
    return (await res.json()) as CohortBundle;
  } catch {
    return null;
  }
}

async function resolveBundle(cohortId: string): Promise<CohortBundle | null> {
  if (typeof window === "undefined") return null;

  const mem = memCache.get(cohortId);
  if (mem && mem.expiresAt > Date.now()) return mem.data;

  const session = readSessionCache(cohortId);
  if (session) {
    memCache.set(cohortId, session);
    return session.data;
  }

  const existing = inflight.get(cohortId);
  if (existing) return existing;

  const promise = (async () => {
    try {
      const data = await fetchBundle(cohortId);
      const ttl = data ? POSITIVE_TTL_MS : NEGATIVE_TTL_MS;
      const entry: CacheEntry = { data, expiresAt: Date.now() + ttl };
      memCache.set(cohortId, entry);
      writeSessionCache(cohortId, entry);
      return data;
    } finally {
      inflight.delete(cohortId);
    }
  })();
  inflight.set(cohortId, promise);
  return promise;
}

/**
 * If `extra` carries a `cohort_id`, fetch the cohort metadata bundle and
 * merge its 9 fields into a new object. Caller-supplied values win over
 * snapshot values. SSR and missing-cohort_id calls return `extra` unchanged
 * without any I/O. Never throws — on any error the original `extra` is
 * returned so tracking still fires.
 */
export async function enrichExtraWithCohort(
  extra: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  if (typeof window === "undefined") return extra;
  const cohortId = extra["cohort_id"];
  if (typeof cohortId !== "string" || cohortId === "") return extra;
  try {
    const bundle = await resolveBundle(cohortId);
    if (!bundle) return extra;
    return { ...bundle, ...extra };
  } catch {
    return extra;
  }
}

export function clearCohortMetadataBrowserCache(): void {
  memCache.clear();
  inflight.clear();
}
